import React, { useContext, useEffect, useRef } from 'react';
import { getIntl } from '../../../../utils/intl';
import { Dynamo } from '../../../../primary-record-types';
import { noteCategoryTagMessages } from '../../../../utils/sharedMessages';
import { AttachmentType } from '../utils/types';
import Stack from '../../../v2BaseComponents/Stack';
import { SelectListPrompt } from '../../../v2BaseComponents/Prompt';
import IconButton from '../../../v2BaseComponents/IconButton';
import { PromptAudioButton } from '../../AudioRecording';
import MedicalProfileUsageIndicator from '../../MedicalProfileUsageIndicator';
import { Toolbar } from '@10play/tentap-editor';
import { Animated, Platform, View } from 'react-native';
import { PRScrollView } from '../../Scroll';
import { IsEditingNoteContentContext } from '../../../v2BaseComponents/Inputs/NoteContentInput';

const intl = getIntl('notes-editor', {
    categoryTags: 'Category Tags',
});

const medicalProfileOrUserNoteCategoryTags: Dynamo.MedicalProfileOrUserNoteCategoryTagType[] = [
    'care-plan',
    'financial',
    'legal',
    'research',
    'routines',
    'supplies',
    'symptom',
];
const organizationClientRecordCategoryTags: Dynamo.OrganizationClientRecordNoteCategoryTagType[] = [
    'assessment',
    'change-in-client',
    'client-preference',
    'instructions',
    'intake',
    'issue',
    'observation',
    'question',
    'reminder',
    'task',
    'visit',
];

export type ActionsOwnProps = {
    isDisabled: boolean;
    onPromptAddImage: () => void;
    onPromptAddFile: () => void;
    tagValue: Dynamo.NoteCategoryTagType[];
    onTagChange: (value: Dynamo.NoteCategoryTagType[]) => void;
    isOrganizationClientRecordNote: boolean;
    defaultOpenAttachmentType?: AttachmentType;
    isAddFilesDisabled: boolean;
    isAddRecordingsDisabled: boolean;
    medicalProfileId: string | null;
    disableRecording: boolean;
    isToolbarVisible: boolean;
    onToggleToolbar: () => void;
};

export type ActionsProps = ActionsOwnProps;

const Actions: React.FC<ActionsProps> = (props) => {
    const [isTagPromptOpen, setIsTagPromptOpen] = React.useState(false);
    const { editor, toolbarItems } = useContext(IsEditingNoteContentContext);
    const [actionsWidth, setActionsWidth] = React.useState(0);

    // Animation values
    const leftButtonsTranslateX = useRef(new Animated.Value(0)).current;
    const audioButtonTranslateX = useRef(new Animated.Value(0)).current;
    const [animationPhase, setAnimationPhase] = React.useState<'visible' | 'hiding' | 'hidden' | 'showing'>('visible');

    useEffect(() => {
        const duration = 500;

        if (props.isToolbarVisible) {
            Animated.parallel([
                Animated.timing(leftButtonsTranslateX, {
                    toValue: -actionsWidth,
                    duration,
                    useNativeDriver: true,
                }),
                Animated.timing(audioButtonTranslateX, {
                    toValue: actionsWidth,
                    duration,
                    useNativeDriver: true,
                }),
            ]).start(() => {
                setAnimationPhase('hidden');
            });
        } else {
            Animated.parallel([
                Animated.timing(leftButtonsTranslateX, {
                    toValue: 0,
                    duration,
                    useNativeDriver: true,
                }),
                Animated.timing(audioButtonTranslateX, {
                    toValue: 0,
                    duration,
                    useNativeDriver: true,
                }),
            ]).start(() => {
                setAnimationPhase('visible');
            });
        }
    }, [props.isToolbarVisible, leftButtonsTranslateX, audioButtonTranslateX, actionsWidth]);

    const renderToolbar = () => {
        if (!props.isToolbarVisible || !editor || !toolbarItems?.length || animationPhase !== 'hidden') {
            return null;
        }

        if (Platform.OS === 'web') {
            return (
                <PRScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    style={{ width: '100%', flexGrow: 0, flexShrink: 1 }}
                >
                    <Toolbar hidden={false} editor={editor} items={toolbarItems} />
                </PRScrollView>
            );
        } else {
            return (
                <View style={{ width: '100%', alignSelf: 'stretch', height: 44 }}>
                    <Toolbar hidden={false} editor={editor} items={toolbarItems} />
                </View>
            );
        }
    };

    const noteCategoryTags = props.isOrganizationClientRecordNote
        ? organizationClientRecordCategoryTags
        : medicalProfileOrUserNoteCategoryTags;

    return (
        <>
            <Stack vertical fullWidth space={2} onLayout={(e) => setActionsWidth(e.nativeEvent.layout.width)}>
                <Stack horizontal fullWidth align="center" justify="space-between">
                    <Animated.View
                        style={{
                            transform: [{ translateX: leftButtonsTranslateX }],
                        }}
                    >
                        {animationPhase !== 'hidden' && (
                            <Stack horizontal justify="flex-start" space={4}>
                                <IconButton
                                    variant="transparent"
                                    icon="Paperclip"
                                    onPress={() => props.onPromptAddFile()}
                                    isDisabled={props.isDisabled || props.isAddFilesDisabled}
                                    testID="prompt-add-attachment"
                                />
                                <IconButton
                                    variant="transparent"
                                    icon="Camera"
                                    onPress={() => props.onPromptAddImage()}
                                    isDisabled={props.isDisabled || props.isAddFilesDisabled}
                                    testID="prompt-add-image"
                                />
                                <IconButton
                                    variant="transparent"
                                    icon="Tag"
                                    onPress={() => setIsTagPromptOpen(true)}
                                    isDisabled={props.isDisabled}
                                    testID="prompt-add-tag"
                                />
                            </Stack>
                        )}
                    </Animated.View>
                    {animationPhase !== 'hiding' && animationPhase !== 'showing' && (
                        <Stack
                            horizontal
                            justify={props.isToolbarVisible ? 'flex-start' : 'center'}
                            space={4}
                            align="center"
                        >
                            <IconButton
                                variant="transparent"
                                icon={props.isToolbarVisible ? 'X' : 'Type'}
                                onPress={() => {
                                    props.onToggleToolbar();
                                    setAnimationPhase((prevState) => {
                                        if (prevState === 'visible') {
                                            return 'hiding';
                                        }
                                        return 'showing';
                                    });
                                }}
                                isDisabled={props.isDisabled}
                                testID="toggle-note-toolbar"
                                style={{ marginLeft: !props.isToolbarVisible ? 8 : 0 }}
                            />
                            {props.isToolbarVisible && (
                                <View
                                    style={{
                                        width: 1,
                                        height: 24,
                                        backgroundColor: '#E0E0E0',
                                        marginLeft: 4,
                                    }}
                                />
                            )}
                        </Stack>
                    )}
                    <View style={{ flex: 1, marginHorizontal: 8 }}>{renderToolbar()}</View>
                    {!props.disableRecording && animationPhase !== 'hidden' && (
                        <Animated.View
                            style={{
                                transform: [{ translateX: audioButtonTranslateX }],
                            }}
                        >
                            <Stack horizontal justify="flex-end" space={4} align="center">
                                {props.medicalProfileId ? (
                                    <MedicalProfileUsageIndicator
                                        medicalProfileId={props.medicalProfileId}
                                        metricType="audio-file-upload"
                                    />
                                ) : null}
                                <PromptAudioButton isDisabled={props.isAddRecordingsDisabled} />
                            </Stack>
                        </Animated.View>
                    )}
                </Stack>
            </Stack>
            <SelectListPrompt
                options={noteCategoryTags.map((tag) => ({
                    label: noteCategoryTagMessages(tag),
                    value: tag,
                }))}
                value={props.tagValue}
                onChange={(_) => props.onTagChange(_ as Dynamo.NoteCategoryTagType[])}
                title={intl('categoryTags')}
                isOpen={isTagPromptOpen}
                setIsOpen={(isOpen) => setIsTagPromptOpen(isOpen)}
                multiSelect
            />
        </>
    );
};

export default React.memo(Actions);
